/**
 * OnboardingHUD - Sidebar progress indicator for tutorial
 * Shows current step, progress, and Resume/Dismiss/Replay controls
 */

import { useTutorial } from '@/contexts/TutorialContext';
import { ChevronRight, ChevronLeft, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { useState } from 'react';
import { ChecklistDialog } from './ChecklistDialog';
// CORE-BL: the HUD counts the filtered sequence.

export const OnboardingHUD = () => {
  const {
    isActive,
    flow,
    currentStep,
    currentStepData,
    completed,
    nextStep,
    previousStep,
    dismissTutorial,
    resumeTutorial,
    replayTutorial,
    activeSteps,
    experienceLabel,
} = useTutorial();

  const navigate = useNavigate();
  const [showChecklist, setShowChecklist] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  // Don't show if no flow, completed, or tutorial is active (controls moved to Coachmark)
  if (!flow || completed || isActive) return null;

  const steps = activeSteps;
  const totalSteps = steps.length;
  const progressPercent = ((currentStep + 1) / totalSteps) * 100;

  return (
    <>
      <Card 
        data-testid="onboarding-hud"
        data-floating-control="onboarding-hud"
        className="border-primary/20 bg-gradient-to-br from-primary/5 to-background"
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              {/* Progress indicator */}
              <div className="flex items-center gap-3 mb-2">
                <div 
                  data-testid="onboarding-hud-progress"
                  className="relative w-10 h-10 flex-shrink-0"
                >
                  <svg className="w-10 h-10 transform -rotate-90">
                    <circle
                      cx="20"
                      cy="20"
                      r="16"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="none"
                      className="text-muted"
                    />
                    <circle
                      cx="20"
                      cy="20"
                      r="16"
                      stroke="currentColor"
                      strokeWidth="3"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 16}`}
                      strokeDashoffset={`${2 * Math.PI * 16 * (1 - progressPercent / 100)}`}
                      className="text-primary transition-all duration-300"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-medium text-primary">
                      {currentStep + 1}/{totalSteps}
                    </span>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground truncate">
                    {/* One label authority. This was a ternary whose only
                        named cases were `manager` and the LEGACY `technician`
                        key, so every current role experience fell through to
                        "User Tutorial" — a technician on
                        `technician_core_v1`, an advisor on
                        `foh_service_writer`, an org admin on
                        `org_overview_v1`. */}
                    {experienceLabel ?? 'Walkthrough'}
                  </h3>
                  <p 
                    data-testid="onboarding-hud-next"
                    className="text-xs text-muted-foreground truncate"
                  >
                    {isActive ? `Next: ${currentStepData?.title}` : 'Resume to continue'}
                  </p>
                </div>
              </div>

              {/* Progress bar */}
              <Progress value={progressPercent} className="h-1.5 mb-3" />

              {/* Action buttons */}
              {isExpanded && (
                <div className="space-y-2">
                  {!isActive && (
                    <Button
                      data-testid="onboarding-hud-resume-btn"
                      onClick={resumeTutorial}
                      size="sm"
                      className="w-full"
                    >
                      Resume
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  
                  {isActive && (
                    <div className="flex gap-2">
                      <Button
                        onClick={previousStep}
                        size="sm"
                        variant="outline"
                        disabled={currentStep === 0}
                        className="flex-1"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        onClick={nextStep}
                        size="sm"
                        disabled={currentStep >= totalSteps - 1}
                        className="flex-1"
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  )}
                  
                  <Button
                    onClick={() => setShowChecklist(true)}
                    size="sm"
                    variant="outline"
                    className="w-full"
                  >
                    Checklist
                  </Button>
                </div>
              )}
            </div>

            {/* More menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="onboarding-hud-more-menu"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0"
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsExpanded(!isExpanded)}>
                  {isExpanded ? 'Collapse' : 'Expand'}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowChecklist(true)}>
                  View Checklist
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="onboarding-hud-replay"
                  onClick={async () => {
                    // The last route-blind replay. `navigate('/')` sent an org
                    // admin — whose walkthrough begins on `/user-guide` — to a
                    // route that answers them with a 403, and it navigated
                    // whether or not the replay had actually started.
                    //
                    // The same defect was fixed in `Coachmark` a pass earlier
                    // and this copy was missed, which is why it is written down
                    // rather than merely corrected: the HUD IS mounted (TopBar
                    // renders it in the fixed sidebar slot), so this was live
                    // for every person who reached it.
                    const result = await replayTutorial();
                    if (result.status !== 'started') return;
                    // The canonical active-step navigation takes them to the
                    // first lesson's own page. There is nothing to guess.
                  }}
                >
                  Replay from Start
                </DropdownMenuItem>
                <DropdownMenuItem onClick={dismissTutorial}>
                  Dismiss
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      <ChecklistDialog 
        open={showChecklist}
        onOpenChange={setShowChecklist}
      />
    </>
  );
};
